import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { getSubscriberFrequency, updateSubscriberFrequency } from '../lib/api';
import { toast } from 'sonner';
import { Mail, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

// HTTP status codes
const HTTP_NOT_FOUND = 404;

// Helper component for result messages (avoids nested ternaries)
function ResultMessage({ status, message }) {
  const config = {
    updated: {
      bgClass: 'bg-green-50 border border-green-200',
      textClass: 'text-green-800',
      Icon: CheckCircle,
      iconClass: 'text-green-600'
    },
    no_change: {
      bgClass: 'bg-blue-50 border border-blue-200',
      textClass: 'text-blue-800',
      Icon: Mail,
      iconClass: 'text-blue-600'
    },
    error: {
      bgClass: 'bg-red-50 border border-red-200',
      textClass: 'text-red-800',
      Icon: AlertCircle,
      iconClass: 'text-red-600'
    }
  };
  
  const { bgClass, textClass, Icon, iconClass } = config[status] || config.error;
  
  return (
    <div 
      className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${bgClass}`}
      data-testid="frequency-result-message"
    >
      <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconClass}`} />
      <p className={`text-sm ${textClass}`}>{message}</p>
    </div>
  );
}

export default function ManageFrequencyPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [subscriber, setSubscriber] = useState(null);
  const [selectedFrequency, setSelectedFrequency] = useState('');
  const [resultMessage, setResultMessage] = useState(null);
  const [resultStatus, setResultStatus] = useState(null);
  
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    
    const validateToken = async () => {
      if (!token) {
        setError('No token provided');
        setLoading(false);
        return;
      }

      try {
        const data = await getSubscriberFrequency(token);
        setSubscriber(data);
        setSelectedFrequency(data.frequency || 'daily');
      } catch (err) {
        if (err.response?.status === HTTP_NOT_FOUND) {
          setError('invalid_token');
        } else {
          setError('An error occurred. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    setResultMessage(null);
    setResultStatus(null);

    try {
      const result = await updateSubscriberFrequency(token, selectedFrequency);
      setResultMessage(result.message);
      setResultStatus(result.status);
      
      if (result.status === 'updated') {
        setSubscriber(prev => ({ ...prev, frequency: result.frequency }));
        toast.success('Frequency updated successfully');
      } else if (result.status === 'no_change') {
        toast.info(result.message);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to update frequency. Please try again.';
      setResultMessage(errorMsg);
      setResultStatus('error');
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F6]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A2E4A] mx-auto mb-4" />
          <p className="text-[#525C6A]">Validating your link...</p>
        </div>
      </div>
    );
  }

  // Error state - invalid token
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-[#F9F9F6]">
        <div className="w-full max-w-lg text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-semibold text-[#1A2E4A] mb-4">Invalid Link</h1>
          <p className="text-[#525C6A] mb-8">
            This link is invalid or has already been used. Please contact us at{' '}
            <a href="mailto:admin@digitallawdigest.com" className="text-[#1A2E4A] underline">
              admin@digitallawdigest.com
            </a>
            .
          </p>
          <Link to="/">
            <Button variant="outline" className="border-[#1A2E4A] text-[#1A2E4A]">
              Return to Homepage
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Frequency descriptions
  const frequencyOptions = [
    {
      value: 'daily',
      label: 'Daily (Monday–Friday)',
      description: 'Receive the digest every weekday morning at 7am UTC'
    },
    {
      value: 'biweekly',
      label: 'Biweekly (Monday & Thursday)',
      description: 'Receive the digest twice a week on Mondays and Thursdays'
    },
    {
      value: 'weekly',
      label: 'Weekly (Monday only)',
      description: 'Receive the digest once a week on Monday mornings'
    }
  ];

  const currentFrequencyLabel = frequencyOptions.find(f => f.value === subscriber?.frequency)?.label || 'Unknown';

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#F9F9F6]">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-6 bg-[#1A2E4A] rounded-full flex items-center justify-center">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <h1 
            className="text-3xl font-semibold text-[#1A2E4A] mb-2"
            data-testid="manage-frequency-heading"
          >
            Manage Your Delivery Frequency
          </h1>
          <p className="text-[#525C6A]">
            You're currently receiving emails: <strong>{currentFrequencyLabel}</strong>
          </p>
        </div>

        {/* Success/Info message */}
        {resultMessage && (
          <ResultMessage status={resultStatus} message={resultMessage} />
        )}

        <div className="bg-white p-8 rounded-lg shadow-sm border border-[#E2E4E0]">
          <div className="mb-6">
            <label className="block text-sm font-medium text-[#0A0F1A] mb-4">
              Select your preferred delivery frequency:
            </label>
            <RadioGroup 
              value={selectedFrequency} 
              onValueChange={setSelectedFrequency}
              className="space-y-3"
              data-testid="frequency-radio-group"
            >
              {frequencyOptions.map((option) => (
                <div 
                  key={option.value}
                  className={`flex items-start space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedFrequency === option.value 
                      ? 'border-[#1A2E4A] bg-[#F5F7FA]' 
                      : 'border-[#E2E4E0] hover:border-[#1A2E4A] bg-white'
                  }`}
                  onClick={() => setSelectedFrequency(option.value)}
                >
                  <RadioGroupItem 
                    value={option.value} 
                    id={option.value}
                    className="mt-1 border-[#E2E4E0] data-[state=checked]:border-[#1A2E4A] data-[state=checked]:bg-[#1A2E4A]"
                    data-testid={`frequency-option-${option.value}`}
                  />
                  <Label htmlFor={option.value} className="cursor-pointer flex-1">
                    <span className="font-medium text-[#0A0F1A] block">{option.label}</span>
                    <span className="text-sm text-[#525C6A] block mt-1">{option.description}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full h-12 bg-[#1A2E4A] hover:bg-[#0A1E3A] text-white"
            data-testid="save-frequency-button"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save changes'
            )}
          </Button>
        </div>

        <p className="text-center text-sm text-[#525C6A] mt-6">
          Need to unsubscribe completely?{' '}
          <Link to={`/unsubscribe?token=${token}`} className="text-[#1A2E4A] underline">
            Click here
          </Link>
        </p>
      </div>
    </div>
  );
}
